import React, { useEffect, useState, useCallback, useRef } from 'react';
import { BiSidebar } from 'react-icons/bi';
import { useNavigate } from 'react-router-dom';
import { IoLogOutOutline, IoMenu, IoClose } from 'react-icons/io5';
import { IoBulbOutline } from 'react-icons/io5';
import { BsFolder2Open, BsQuestionCircle } from 'react-icons/bs';
import { IoIosArrowDown } from 'react-icons/io';
import { pages } from '@microsoft/teams-js'; // ✅ Required

import { API_URL } from '../../services/api';
import { DataState } from '../../context/DataProvider';
import Tabbar from '../base/Tabbar';
import { useTeams } from '../../context/TeamsProvider';
import ActivityIndicator from './ActivityIndicator'; // Import the ActivityIndicator component
import ProfileSettingsModal from '../ProfileSettingsModal';
const TEAMS_APP_ID = process.env.REACT_APP_TEAMS_APP_ID;

const BaseLayout = ({
  leftSide,
  children,
  tabBarAbsolute = true,
  createName,
  chatHistory,
  selectedChatHistory,
  handleSelectChat,
  handleCreate,
  hideLeftHeader = false,
  tabName,
}) => {
  const navigate = useNavigate();
  const { isTeamsInitialized } = useTeams();
  const {
    setFeedbackModalOpen,
    setJustChangedLocalStorageData,
    toggleSidebar: sidebarCollapsed,
    setToggleSidebar,
    userInfo,
    rightSidebarWidth,
    setRightSidebarWidth,
  } = DataState();
  const [openSettings, setOpenSettings] = useState(false);
  // const [toggleSidebar, setToggleSidebar] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const toggleSidebar = tabName === 'communication' ? true : sidebarCollapsed;

  // --- State and Refs for Resizable Sidebar ---

  const isResizing = useRef(false);
  const resizableContainerRef = useRef(null);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    isResizing.current = true;
    document.body.style.cursor = 'col-resize'; // Change cursor for the whole page
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    // eslint-disable-next-line
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isResizing.current || !resizableContainerRef.current) return;
    const containerRect = resizableContainerRef.current.getBoundingClientRect();

    // The handle itself is 8px wide (w-2). To make the drag feel centered,
    // we offset the calculation by half the handle's width.
    const handleWidth = 8;
    const newWidth = containerRect.right - e.clientX - handleWidth / 2;

    if (newWidth >= 200 && newWidth <= 500) {
      setRightSidebarWidth(newWidth);
    }
    // eslint-disable-next-line
  }, []);

  const handleMouseUp = useCallback(() => {
    isResizing.current = false;
    document.body.style.cursor = 'default'; // Reset cursor
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    // eslint-disable-next-line
  }, []);

  const handleProjectsButtonClick = () => {
    if (!isTeamsInitialized) {
      navigate('/projects');
      return;
    }

    const targetPageId = 'projects';

    if (pages.currentApp.isSupported()) {
      pages.currentApp
        .navigateTo({
          pageId: targetPageId,
          // subPageId: targetSubPageId, // Use if you need to pass internal state/route
        })
        .then(() => {})
        .catch((error) => {});
    } else {
      pages
        .navigateToApp({
          appId: TEAMS_APP_ID,
          pageId: targetPageId,
          // subPageId: targetSubPageId,
        })
        .then(() => {})
        .catch((error) => {});
    }
  };

  const handleLogout = () => {
    async function logout() {
      try {
        await fetch(`${API_URL}/logout`, {
          method: 'POST',
          credentials: 'include', // 🔥 important! send cookies with the request
          headers: {
            'Content-Type': 'application/json',
          },
        });
      } catch (error) {
      } finally {
        localStorage.clear();
        setJustChangedLocalStorageData(true);
        navigate('/login');
      }
    }
    logout();
  };

  useEffect(() => {
    const storedUserGoogleId = localStorage.getItem('projectToken');
    if (!storedUserGoogleId) {
      navigate('/login');
    }
  }, [navigate]);

  const SidebarContent = () => (
    <div className="bg-1 flex h-full flex-col justify-between gap-1 overflow-hidden rounded-2xl">
      <div className="relative flex flex-col items-center gap-3 px-2 py-4 ">
        {/* Mobile Header with Close Button */}
        <div className="mb-2 flex w-full items-center justify-between sm:hidden">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setMobileSidebarOpen(false);
            }}
            className="absolute right-0 top-0 rounded-lg p-1 transition-colors hover:bg-white hover:bg-opacity-10"
          >
            <IoClose className="text-xl" />
          </button>
        </div>
        {/* <img
          src={theme === "light" ? "../assets/logo.png" : "../assets/logo.png"}
          alt=""
          className="mb-2 md:mb-4 w-full text-center h-8 md:h-10"
        /> */}
        {/* Logo Container */}
        <div className="mb-1 flex w-full items-center justify-center gap-3 md:mb-4">
          {/* Main Logo (Always visible) */}
          <img
            src={`../assets/logo.png`}
            alt="Main Logo"
            className={`${toggleSidebar ? 'h-8 md:h-10' : 'h-10 md:h-12'} transform object-contain transition-all duration-100`}
          />

          {/* Partner Logo (Conditionally renders if it exists) */}
          {userInfo?.partnerLogo && !toggleSidebar && (
            <>
              {/* Visual Separator for clarity */}
              <div
                className="h-6 border-l border-white/30"
                aria-hidden="true"
              ></div>

              <img
                src={`../assets/partners/${userInfo.partnerLogo}`}
                alt="Partner Logo"
                className="h-7 object-contain md:h-9"
              />
            </>
          )}
        </div>
        <div className="flex w-full flex-col gap-4">
          <button
            onClick={handleCreate}
            className={`flex cursor-pointer items-center justify-center 
              gap-2 rounded-xl bg-primary-gradient px-3 py-3 text-sm 
              text-white transition-all duration-200 hover:opacity-90 disabled:pointer-events-none disabled:opacity-50 sm:text-sm md:py-2 md:text-xs 
              ${toggleSidebar && 'text-xl font-bold'}`}
          >
            {toggleSidebar ? '+' : createName}
          </button>

          {chatHistory && !toggleSidebar && (
            <button
              className="flex items-center justify-between gap-1 px-2 font-semibold"
              onClick={() => setShowHistory(!showHistory)}
            >
              Recent Chats{' '}
              <IoIosArrowDown
                className={`${showHistory ? 'rotate-180' : ''}`}
              />
            </button>
          )}
        </div>
      </div>

      {chatHistory && !toggleSidebar && showHistory && (
        <div className="hide-sidebar bg-2 mx-2 -mt-3 flex flex-1 flex-col gap-2 overflow-y-auto rounded-xl p-3">
          {chatHistory.length > 0 ? (
            chatHistory.map((task) => (
              // eslint-disable-next-line
              <div
                key={task._id}
                className="relative"
                onClick={() => handleSelectChat(task)}
              >
                <div
                  className={`${
                    task._id === selectedChatHistory
                      ? 'bg-white bg-opacity-15'
                      : 'bg-white bg-opacity-5'
                  } flex cursor-pointer items-center gap-2 rounded-lg px-3 py-1 transition-all duration-200 hover:bg-zinc-800`}
                >
                  <p className="overflow-hidden text-ellipsis whitespace-nowrap text-sm">
                    {task.chatName}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <div className="flex flex-col gap-3 p-2 text-center">
              No recent chats
            </div>
          )}
        </div>
      )}

      <div className="text-s-text flex w-full flex-col gap-1 px-2 py-3">
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setOpenSettings(true);
            // Close sidebar on mobile
            if (window.innerWidth < 768) {
              setMobileSidebarOpen(false);
            }
          }}
          className={`flex w-full gap-3 rounded-lg ${toggleSidebar ? 'justify-center' : 'justify-start'} items-center p-3 transition-all duration-200 hover:bg-white hover:bg-opacity-10 md:p-2`}
        >
          {userInfo && (
            <div className="flex items-center gap-3">
              {userInfo.picture ? (
                <img
                  src={userInfo.picture}
                  alt={userInfo.name}
                  className="h-[25px] w-[25px] rounded-full object-cover"
                />
              ) : (
                <div className="flex h-[25px] w-[25px] items-center justify-center rounded-full bg-gray-300 text-sm font-bold text-gray-600">
                  {userInfo.name
                    ? userInfo.name?.charAt(0).toUpperCase()
                    : userInfo.email?.charAt(0).toUpperCase()}
                </div>
              )}
              <span
                className={`max-w-20 overflow-hidden text-ellipsis whitespace-nowrap text-sm md:max-w-24 ${toggleSidebar && 'hidden'}`}
              >
                {userInfo.name || userInfo.email}
              </span>
            </div>
          )}
        </button>
        {/* <button
          className={`rounded-lg gap-3 flex ${
            toggleSidebar ? "justify-center" : "justify-start"
          } items-center p-2`}
        >
          <IoSettingsOutline className="text-2xl" />
          {!toggleSidebar && "Settings"}
        </button> */}

        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleProjectsButtonClick();
          }}
          className={`flex gap-3 rounded-lg ${
            toggleSidebar ? 'justify-center' : 'justify-start'
          } items-center p-3 transition-all duration-200 hover:bg-white hover:bg-opacity-10 md:p-2`}
        >
          <BsFolder2Open className="flex-shrink-0 text-xl md:text-2xl" />
          {!toggleSidebar && (
            <span className="text-sm md:text-sm">Projects</span>
          )}
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleLogout();
          }}
          className={`flex gap-3 rounded-lg ${
            toggleSidebar ? 'justify-center' : 'justify-start'
          } items-center p-3 transition-all duration-200 hover:bg-white hover:bg-opacity-10 md:p-2`}
        >
          <IoLogOutOutline className="flex-shrink-0 text-xl md:text-2xl" />
          {!toggleSidebar && (
            <span className="text-sm md:text-sm">Log out</span>
          )}
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setFeedbackModalOpen(true);
            // Close sidebar on mobile
          }}
          className={`flex gap-3 rounded-lg ${
            toggleSidebar ? 'justify-center' : 'justify-start'
          } items-center p-3 transition-all duration-200 hover:bg-white hover:bg-opacity-10 md:p-2`}
        >
          <IoBulbOutline className="flex-shrink-0 text-xl md:text-2xl" />
          {!toggleSidebar && (
            <span className="text-sm md:text-sm">Feedback</span>
          )}
        </button>

        <a
          href="mailto:nirmalnambiar@oqlous.com"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            window.location.href = 'mailto:nirmalnambiar@oqlous.com';
          }}
          className={`flex gap-3 rounded-lg ${
            toggleSidebar ? 'justify-center' : 'justify-start'
          } items-center p-3 transition-all duration-200 hover:bg-white hover:bg-opacity-10 md:p-2`}
        >
          <BsQuestionCircle className="flex-shrink-0 text-xl md:text-2xl" />
          {!toggleSidebar && <span className="text-sm md:text-sm">Help</span>}
        </a>
      </div>

      <ProfileSettingsModal
        isOpen={openSettings}
        onClose={() => setOpenSettings(false)}
        userInfo={userInfo}
      />
    </div>
  );

  return (
    <div className="bg-3 text-p-text relative flex h-screen max-h-[100svh] w-screen flex-col gap-2 overflow-hidden p-2 pt-4 md:flex-row md:gap-2 md:p-2">
      {/* Desktop Sidebar */}
      <div
        className={`${
          toggleSidebar ? 'hidden md:block md:w-16' : 'hidden md:block md:w-56'
        } relative h-full flex-shrink-0 transform overflow-hidden transition-all duration-150`}
      >
        {!toggleSidebar && !userInfo?.partnerLogo && (
          <button
            className="absolute left-2 top-2 z-10 rounded-lg p-1 text-xl transition-colors hover:bg-white hover:bg-opacity-10 md:text-2xl"
            onClick={() => setToggleSidebar(!toggleSidebar)}
          >
            <BiSidebar />
          </button>
        )}
        <SidebarContent />
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        // eslint-disable-next-line
        <div
          className="fixed inset-0 z-[200] bg-black bg-opacity-50 sm:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        >
          {/* eslint-disable-next-line */}
          <div
            className="bg-1 h-full w-64 p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <SidebarContent />
          </div>
        </div>
      )}

      {/* NEW: Wrapper for the main resizable area */}
      <div ref={resizableContainerRef} className="flex h-full min-w-0 flex-1">
        {/* Main Content */}
        <div className="bg-1 relative flex h-full flex-1 flex-col gap-7 overflow-hidden rounded-2xl border border-zinc-700 p-2 sm:p-4">
          {tabName !== 'communication' &&
            (toggleSidebar || userInfo?.partnerLogo) && (
              <button
                className="absolute left-2 top-2 z-10 hidden rounded-lg p-1 text-lg transition-colors hover:bg-white hover:bg-opacity-10 md:block md:text-xl"
                onClick={() => setToggleSidebar(!toggleSidebar)}
              >
                <BiSidebar />
              </button>
            )}
          {/* Mobile Sidebar Toggle Button */}
          <button
            className="absolute left-2 top-2 z-30 text-2xl sm:hidden"
            onClick={() => {
              setMobileSidebarOpen(true);
              setToggleSidebar(false);
            }}
          >
            <IoMenu />
          </button>
          <Tabbar absolute={tabBarAbsolute} />
          {children}
        </div>
        {leftSide && (
          <>
            {/* MODIFIED: Draggable Handle - This now defines the separation space */}
            {/* eslint-disable-next-line */}
            <div
              className="group hidden w-2 flex-shrink-0 cursor-col-resize items-center justify-center md:flex"
              onMouseDown={handleMouseDown}
            >
              <div className="h-12 w-0.5 rounded-full bg-zinc-700 transition-colors duration-200 group-hover:bg-[#5652CF]" />
            </div>

            <div
              className="hidden h-full overflow-hidden md:block"
              style={{ width: `${rightSidebarWidth}px` }}
            >
              <div className="bg-1 flex h-full flex-col rounded-xl border border-zinc-800 shadow-lg">
                {/* Header */}
                <div className="flex h-14 items-center justify-between border-b border-zinc-700 p-4">
                  <h1 className="text-sm font-bold tracking-wide text-zinc-200">
                    Workspace
                  </h1>
                  {/* {(
                <div className="px-3 py-1 rounded-full flex items-center gap-2 bg-blue-900/50 border border-blue-700/60">
                  <div className="bg-blue-400 h-2 w-2 rounded-full animate-pulse"></div>
                  <span className="text-xs font-medium text-blue-300">Running</span>
                </div>
              )} */}
                  <ActivityIndicator />
                </div>
                {/* Conditionally render the heading */}
                {/* {!hideLeftHeader && (
              <>
                <div className="flex items-center justify-between mb-3">
                  <h1 className="text-base font-bold ">Oqlous Workspace</h1>
                </div>
              </>
            )} */}

                {/* <div
              className={`flex flex-col gap-3 rounded-3xl flex-1 overflow-hidden ${hideLeftHeader ? "" : "bg-2"
                }`}
            > */}
                {/* {!hideLeftHeader && (
                <div className="flex items-center justify-between w-full br-1 border-b border-opacity-20 py-3 px-5">
                 
                  <div className="flex items-center gap-3 ">
                  <div className="bg-[#00A01C] h-3 w-3 rounded-full hover:scale-105"></div>
                  <span className="text-base">Planning</span>
                </div>
                </div>
              )} */}
                {leftSide}
                {/* </div> */}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
export default BaseLayout;
